import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { CreditCard, Loader2, ShieldCheck, Trash2, Webhook, Copy } from "lucide-react";

export function StripeKeyCard({ pin }) {
  const [status, setStatus] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [webhookSecret, setWebhookSecret] = useState("");
  const [savingWebhook, setSavingWebhook] = useState(false);
  const headers = { "X-Admin-Pin": pin };
  const webhookUrl = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/webhook/stripe`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stripe-key", { headers });
      setStatus(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!keyInput.trim()) {
      toast.error("Paste your Stripe secret key first");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/stripe-key", { api_key: keyInput.trim() }, { headers });
      setStatus(data);
      setKeyInput("");
      toast.success(`Stripe ${data.mode === "live" ? "LIVE" : "test"} key saved securely.`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      const { data } = await api.delete("/admin/stripe-key", { headers });
      setStatus(data);
      setTestResult(null);
      toast.success("Stripe key removed — reverted to the default key.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/stripe-key/test", {}, { headers });
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: apiError(err) });
    } finally {
      setTesting(false);
    }
  };

  const saveWebhook = async () => {
    if (!webhookSecret.trim()) {
      toast.error("Paste your Stripe webhook signing secret first");
      return;
    }
    setSavingWebhook(true);
    try {
      await api.post("/admin/stripe-webhook-secret", { webhook_secret: webhookSecret.trim() }, { headers });
      setWebhookSecret("");
      toast.success("Webhook signing secret saved securely.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingWebhook(false);
    }
  };

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast.success("Webhook URL copied");
    } catch {
      toast.message(webhookUrl);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="admin-stripe-card">
      <h2 className="font-head font-bold text-lg flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-emerald-400" /> Stripe payments
      </h2>
      <p className="text-sm text-zinc-500 mt-1">
        Paste your Stripe <span className="text-zinc-300">secret</span> key (<code className="text-zinc-400">sk_live_…</code> for real payments,{" "}
        <code className="text-zinc-400">sk_test_…</code> to test). Stored encrypted on the server.
      </p>

      <div className="mt-4 flex items-center gap-2 text-sm" data-testid="admin-stripe-status">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        ) : status?.configured ? (
          <>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${status.mode === "live" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25" : "bg-yellow-500/15 text-yellow-400 border border-yellow-500/25"}`}>
              <ShieldCheck className="h-3.5 w-3.5" /> {status.mode === "live" ? "LIVE mode" : "Test mode"}
            </span>
            <span className="text-zinc-400 font-mono">{status.masked}</span>
          </>
        ) : (
          <span className="text-zinc-500">No custom key set — using the default (test) key.</span>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <input
          type="password"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          placeholder="sk_live_…"
          autoComplete="off"
          data-testid="admin-stripe-key-input"
          className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-stripe-save-btn"
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 px-4 py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Save key
        </button>
        {status?.configured && (
          <button
            onClick={remove}
            disabled={saving}
            data-testid="admin-stripe-remove-btn"
            title="Remove the custom key and revert to the default"
            className="inline-flex items-center justify-center bg-white/5 border border-white/10 hover:bg-white/10 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4 text-red-400" />
          </button>
        )}
      </div>
      <p className="text-[11px] text-zinc-600 mt-2">
        After saving a live key here, do one real test purchase to confirm, then refund yourself in the Stripe dashboard.
      </p>

      <div className="mt-3 pt-3 border-t border-white/10">
        <button
          onClick={test}
          disabled={testing}
          data-testid="admin-stripe-test-btn"
          className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Test key against Stripe
        </button>
        {testResult && (
          <div
            data-testid="admin-stripe-test-result"
            className={`mt-3 rounded-md border p-3 text-sm ${testResult.ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`}
          >
            {testResult.ok ? (
              <span>✅ {testResult.message} <span className="text-zinc-400">({testResult.mode} key {testResult.masked}, source: {testResult.source})</span></span>
            ) : (
              <div>
                <div className="font-semibold">❌ Stripe rejected this{testResult.mode ? ` ${testResult.mode}` : ""} key{testResult.masked ? ` (${testResult.masked}, source: ${testResult.source})` : ""}:</div>
                <div className="mt-1 font-mono text-xs break-words text-red-200">{testResult.error}</div>
                {(testResult.key_length !== undefined) && (
                  <div className="mt-2 text-[11px] text-zinc-400">
                    Key length: <b>{testResult.key_length}</b> chars {testResult.key_length < 100 ? "⚠️ (a live secret key is usually ~107 chars — yours looks truncated/incomplete)" : "(full length ✓)"} · sent to: <span className="font-mono">{testResult.api_base_used}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-5 pt-4 border-t border-white/10" data-testid="admin-stripe-webhook">
        <h3 className="font-head font-semibold text-sm flex items-center gap-2">
          <Webhook className="h-4 w-4 text-emerald-400" /> Recurring webhook
          {!loading && (
            status?.webhook_configured ? (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                <ShieldCheck className="h-3 w-3" /> Connected
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-yellow-500/15 text-yellow-400 border border-yellow-500/25">
                Not set
              </span>
            )
          )}
        </h3>
        <p className="text-sm text-zinc-500 mt-1">
          Required so monthly renewals auto-add credits and cancellations sync. In Stripe → <span className="text-zinc-300">Developers → Webhooks</span>, add an endpoint pointing to the URL below (events: <code className="text-zinc-400">invoice.paid</code>, <code className="text-zinc-400">invoice.payment_failed</code>, <code className="text-zinc-400">customer.subscription.deleted/updated</code>), then paste its <code className="text-zinc-400">whsec_…</code> signing secret here.
        </p>

        <div className="mt-3 flex items-center gap-2">
          <code className="flex-1 truncate bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-xs text-zinc-400" data-testid="admin-stripe-webhook-url">{webhookUrl}</code>
          <button
            onClick={copyWebhookUrl}
            data-testid="admin-stripe-webhook-copy"
            className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 hover:bg-white/10 px-3 py-2 rounded-md text-xs font-semibold transition-colors"
          >
            <Copy className="h-3.5 w-3.5" /> Copy
          </button>
        </div>

        <div className="mt-3 flex gap-2">
          <input
            type="password"
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.target.value)}
            placeholder="whsec_…"
            autoComplete="off"
            data-testid="admin-stripe-webhook-input"
            className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            onClick={saveWebhook}
            disabled={savingWebhook}
            data-testid="admin-stripe-webhook-save-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 px-4 py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {savingWebhook ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Save secret
          </button>
        </div>
      </div>
    </div>
  );
}
